/**
 * A money amount as an input shows it with thousands separators ("1,000,000")
 * and the plain number the form stores (1000000). Pure: the mapping both ways,
 * and what the input shows while the person types.
 */

const wholeDollars = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});
const withCents = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * The amount with thousands separators: 1000000 → "1,000,000", 1234.5 →
 * "1,234.50" (cents show both digits, whole dollars none), null → "".
 */
export function formatMoneyText(amount: number | null): string {
  if (amount === null || !Number.isFinite(amount)) return "";
  return (Number.isInteger(amount) ? wholeDollars : withCents).format(amount);
}

/**
 * What a typed text means, its thousands separators ignored. `amount` is null
 * while nothing is typed yet ("", ".", "-"); not `valid` for text that cannot
 * become an amount ("12a", a third decimal, a separator after the point), so
 * the input refuses that keystroke.
 */
export type MoneyTextReading =
  | { valid: true; amount: number | null }
  | { valid: false };

/** "1,000,000" → 1000000; "" → null; "1.234" → not valid. */
export function parseMoneyText(
  text: string,
  allowNegative = false,
): MoneyTextReading {
  const shape = allowNegative ? /^-?[\d,]*\.?\d{0,2}$/ : /^[\d,]*\.?\d{0,2}$/;
  if (!shape.test(text)) return { valid: false };
  const plain = text.replace(/,/g, "");
  if (plain === "" || plain === "." || plain === "-" || plain === "-.") {
    return { valid: true, amount: null };
  }
  const amount = Number(plain);
  return Number.isFinite(amount) ? { valid: true, amount } : { valid: false };
}

/**
 * What the input shows: the person's own text while they type and it still
 * means the stored amount (so the caret never jumps and "1,000,0005" can be
 * typed through), else the amount with its separators.
 *
 * @param typed - The text typed since the input took focus; null when not typing.
 */
export function moneyInputTextOf(
  typed: string | null,
  amount: number | null,
  allowNegative = false,
): string {
  if (typed !== null) {
    const reading = parseMoneyText(typed, allowNegative);
    if (reading.valid && reading.amount === amount) return typed;
  }
  return formatMoneyText(amount);
}
